import * as CL from '@design-system/component-library';
import { DetailsWithCard } from '../DetailswithCard/DetailsWithCard.js';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { Table } from '../Table/Table.js';
import {
  addInternetSubscription,
  addSubscriptionMsg,
  addVoiceSubscription,
  addressOrNumberMsg,
  identifierMsg,
  internetSubscriptionsMsg,
  noInternetSubscriptionsMsg,
  noVoiceSubscriptionsMsg,
  omaElisaForEmployeeMsg,
  phoneNumberMsg,
  pukCodeMsg,
  subscriptionMissingQuestionMsg,
  subscriptionTypeMsg,
  subscriptionsInstructions1Msg,
  subscriptionsInstructions2Msg,
  subscriptionsInstructions3Msg,
  subscriptionsInstructionsOwnInfoLinkMsg,
  t,
  unknownMsg,
  userOfSubscriptionMsg,
  voiceAndBroadbandMsg,
  voiceSubscriptionsMsg,
} from '../../common/i18n/index.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { parseMobileSubscriptionAttrs } from '../EmployeeSubscriptionDetails/employeeSubscriptionUtils.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useSelector } from 'react-redux';
import { useTitle } from '../../common/hooks/useTitle.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { State } from '../../selfservice/common/store.js';

import './EmployeeSubscriptions.scss';

const getVoiceColumns = () => [
  {
    key: 'subscriptionUser',
    label: t.HM82(userOfSubscriptionMsg),
  },
  { key: 'phoneNumber', label: t.FRYN(phoneNumberMsg) },
  { key: 'pukCode', label: t.K5TL(pukCodeMsg) },
];

const getVoiceRows = (voiceSubs: EmployeeSubscription[] = []) => {
  return voiceSubs.map(subscription => {
    const effectivePath = `${generatePath(paths.EMPLOYEE_VOICE_SUBSCRIPTION, { subscriptionId: subscription.subscriptionId })}`;
    const mobileAttributes = parseMobileSubscriptionAttrs(subscription);
    return {
      subscriptionUser: (
        <Link to={effectivePath}>
          <div>
            {subscription.subscriptionContactName ?? subscription.subscriptionPurposeOfUse ?? t.QWC8(unknownMsg)}
          </div>
        </Link>
      ),
      phoneNumber: mobileAttributes.mobileNumber,
      pukCode: mobileAttributes.puk1,
    };
  });
};

const getBroadbandColumns = () => [
  {
    key: 'subscriptionType',
    label: t.QH8P(subscriptionTypeMsg),
  },
  { key: 'addressOrNumber', label: t.LK7B(addressOrNumberMsg) },
  { key: 'identifier', label: t.L9QG(identifierMsg) },
];

const getBroadbandRows = (broadbandSubs: EmployeeSubscription[] = []) => {
  return broadbandSubs.map(subscription => {
    const effectivePath = `${generatePath(paths.EMPLOYEE_BROADBAND_SUBSCRIPTION, { subscriptionId: subscription.subscriptionId })}`;
    return {
      subscriptionType: (
        <Link to={effectivePath}>
          <div>{subscription.subscriptionName}</div>
        </Link>
      ),
      addressOrNumber: subscription.subscriptionUserFriendlyId ?? '',
      identifier: subscription.subscriptionContactName ?? '',
    };
  });
};

const EmployeeSubscriptionInstructionsCard = () => (
  <>
    <h3>{t.C6Z0(subscriptionMissingQuestionMsg)}</h3>
    <p>
      {t.RA43(subscriptionsInstructions1Msg)}
      <strong>{t.W45M(addSubscriptionMsg)}</strong>
      {t.EY68(subscriptionsInstructions2Msg)}
      <Link to={generatePath(paths.EMPLOYEE_OWN_INFO)}>{t.EC1N(subscriptionsInstructionsOwnInfoLinkMsg)}</Link>
      {t.ZXBT(subscriptionsInstructions3Msg)}
    </p>
  </>
);

const AddSubscriptionSection = () => {
  const navigate = useNavigate();
  return (
    <>
      <h3>{t.W45M(addSubscriptionMsg)}</h3>
      <CL.LinkBlock
        title={t.FIAV(addVoiceSubscription)}
        icon={<CL.Icon icon="smartphone" />}
        onClick={() => navigate(paths.EMPLOYEE_VOICE_SUBSCRIPTION_ADD)}
      />
      <CL.LinkBlock
        title={t.W9UE(addInternetSubscription)}
        icon={<CL.Icon icon="router" />}
        onClick={() => navigate(paths.EMPLOYEE_BROADBAND_SUBSCRIPTION_ADD)}
      />
    </>
  );
};

export const EmployeeVoiceAndBroadbandSubscriptions = () => {
  const { voiceSubscriptions, broadbandSubscriptions } = useSelector(
    (state: State) => ({
      voiceSubscriptions: state?.user?.authenticated?.subscriptions?.voice,
      broadbandSubscriptions: state?.user?.authenticated?.subscriptions?.broadband,
    }),
    deepEqual
  );

  useTitle([t.H6JV(omaElisaForEmployeeMsg), t.JPRH(voiceAndBroadbandMsg)]);

  return (
    <div className="of-employee-subscriptions">
      <DetailsWithCard cardContent={<EmployeeSubscriptionInstructionsCard />} cardPicto="add-sim">
        <h3>{t.AR0B(voiceSubscriptionsMsg)}</h3>
        <Table
          columns={getVoiceColumns()}
          noItemsText={t.IL9T(noVoiceSubscriptionsMsg)}
          rows={getVoiceRows(voiceSubscriptions)}
          tableType="bordered"
          noMarginTop
        />
        <h3>{t.SEYV(internetSubscriptionsMsg)}</h3>
        <Table
          columns={getBroadbandColumns()}
          noItemsText={t.UF2D(noInternetSubscriptionsMsg)}
          rows={getBroadbandRows(broadbandSubscriptions)}
          tableType="bordered"
          noMarginTop
        />
        <AddSubscriptionSection />
      </DetailsWithCard>
    </div>
  );
};
